import { LinearProgress, Stack } from "@mui/material";
import type { Dayjs } from "dayjs";
import take from "lodash/take";
import type { FC } from "react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import InfiniteScroll from "../components/infiniteScroll/InfiniteScroll";
import { useLimit } from "../misc/useLimit";
import { useOpenInNewTab } from "../misc/useOpenInNewTab";
import { useQuery } from "../misc/useQuery";
import type { CostCardCost } from "./CostCard";
import { CostCard } from "./CostCard";
import type { CostSearchCriteria } from "./CostSearch";
import CostSearch from "./CostSearch";
import CostsActions from "./CostsActions";
import { filterCosts } from "./filterCosts";
import { useCosts } from "./useCosts";
import type { PdfExtractDialogResult } from "./edit/PdfExtractDialog";
import { PdfExtractDialog } from "./edit/PdfExtractDialog";
import { useAddInvoiceMutation, useUpdateCostMutation } from "./edit/useCost";

interface Props {
  readonly search: CostSearchCriteria;
  onSearchChange: (value: CostSearchCriteria) => void;
}

const CostsPage: FC<Props> = ({ search, onSearchChange }) => {
  const navigate = useNavigate();
  const { openChildInNewTab } = useOpenInNewTab();
  const { limit, loadMore, resetLimit } = useLimit();
  const [showPaid, setShowPaid] = useState(false);
  const { costs, isLoading } = useCosts({ from: search.month!.format("YYYY-MM"), showPaid });

  const searchQuery = useQuery();
  useEffect(() => {
    const query = searchQuery.get("query");
    if (query) {
      onSearchChange({ text: query, month: search.month });
    }
  }, []);

  const handleQueryChange = (query: { text: string; month: Dayjs | null }) => {
    resetLimit();
    onSearchChange(query);
  };
  const [pdfFile, setPdfFile] = useState<{ file: File; cost: CostCardCost }>();
  const addInvoice = useAddInvoiceMutation();
  const updateCost = useUpdateCostMutation();
  const handleDetect = async (detectedData: PdfExtractDialogResult) => {
    if (pdfFile && detectedData.invoiceNumber && detectedData.date && detectedData.amount) {
      await addInvoice({
        invoiceData: {
          invoiceNumber: detectedData.invoiceNumber,
          date: detectedData.date,
          amount: detectedData.amount,
          file: pdfFile.file,
          status: detectedData.status ?? "FOR_PAYMENT",
          exchangeRate: detectedData.exchangeRate,
        },
        costId: pdfFile.cost.id,
      });
    }
    setPdfFile(undefined);
  };

  const filteredCosts = filterCosts(costs, search.text.toLowerCase());

  return (
    <Stack
      spacing={1}
      sx={{
        marginTop: 1,
        height: "100%",
      }}
    >
      <CostSearch search={search} onChange={handleQueryChange} showPaid={showPaid} onShowPaidChange={setShowPaid} />
      {isLoading && <LinearProgress />}
      <InfiniteScroll
        dataLength={limit < filteredCosts.length ? limit : filteredCosts.length}
        fetchData={loadMore}
        hasMore={limit < filteredCosts.length}
      >
        {take(filteredCosts, limit).map((c) => (
          <CostCard
            key={c.id}
            value={c}
            onClick={() => navigate(`/costs/${encodeURIComponent(c.id)}`)}
            onContextMenu={() => openChildInNewTab(encodeURIComponent(c.id))}
            onPdfDropped={setPdfFile}
          />
        ))}
      </InfiniteScroll>
      <CostsActions values={filteredCosts} />
      <PdfExtractDialog
        open={!!pdfFile}
        onClose={() => setPdfFile(undefined)}
        file={pdfFile?.file}
        cost={pdfFile?.cost}
        onDetect={handleDetect}
        onChangeCurrency={async (currency) => {
          const updated = await updateCost(pdfFile!.cost.id, { currency });
          setPdfFile((p) => ({
            ...p!,
            cost: {
              ...p!.cost,
              currency: updated.currency,
            },
          }));
        }}
      />
    </Stack>
  );
};

export default CostsPage;
